<template>
  <div id="tableWrapper">
    <loading-notification
      v-if="loading === 1 && refreshing === 0"
      :message="loadingMessage">
    </loading-notification>

    <template v-else>
      <h2 v-if="table" class="title is-3">Tabel | {{ table.name }}</h2>

      <div class="buttons is-pulled-right">
        <options-buttons
          :custom="toolbarButtons"
          :editing="false"
          :deleting="showMassRemoveButton"
          deleteTitle="Geselecteerde rijen verwijderen"
          @delete="massRemove">
        </options-buttons>
      </div>

      <table
        id="headerTable"
        class="table is-fullwidth is-bordered">

        <thead>
          <tr>
            <th class="header-column">
              <toggle-all
                :checked="allSelected"
                :indeterminate="someSelected"
                @toggle="toggleAllSelected()">
              </toggle-all>
            </th>

            <column-header
              v-for="(column, idx) in columns"
              :key="column.column.id"
              :column="column"
              :index="idx"
              :filter-list-height="filterListHeight"
              my-class="header-column">
            </column-header>
            <th class="header-column"></th>
          </tr>
        </thead>
      </table>

      <table id="dataTable" v-if="loaded" class="table is-fullwidth is-striped is-bordered">
        <table-row
          v-for="(row, idx) in filteredDatasets"
          :firstRow="idx === 0"
          :key="row.id"
          :row="parse(row.columns)"
          :editing="true"
          :deleted="row.deleted_at"
          :datasetId="row.id"
          :withTrashed="withTrashed"
          :removable="isRemovable(row.id)"
          @edit="edit(row.id)"
          @delete="remove(row.id)"
          @restore="restore(row.id)"
          @duplicate="duplicateFrom(row.id)"
          @toggleRemove="toggleRemove(row.id)"
          @tableReady="setColumnWidths">
        </table-row>

        <tr>
          <th class="guide-column">
            <toggle-all
                :checked="allSelected"
                :indeterminate="someSelected"
                @toggle="toggleAllSelected()">
            </toggle-all>
          </th>

          <column-header
              v-for="(column, idx) in columns"
              :key="column.column.id"
              :column="column"
              :index="idx"
              :filter-list-height="filterListHeight"
              my-class="guide-column">
          </column-header>
          <th class="guide-column"></th>
        </tr>
      </table>

      <loading-notification
        v-if="loading > 1"
        :message="loadingMessage">
      </loading-notification>

      <import-datasets
        v-if="isAdmin(account) || inScope(account, 11)"
        :isActive="!!importDatasets"
        :source="(!!importDatasets) ? importDatasets : ''"
        @close="toggleImport(false)">
      </import-datasets>

      <edit-row
        v-if="!withTrashed"
        :isActive="editRowIsActive"
        :datasetId="datasetId"
        :duplicate="duplicate"
        @save="save"
        @close="close">
      </edit-row>

      <combinator
        v-if="(isAdmin(account) || inScope(account, 12)) && !withTrashed"
        :isActive="combinatorIsActive"
        :hasSelected="hasSelected"
        @close="toggleCombinator(false)">
      </combinator>

      <progress-bar :progress="progress"></progress-bar>
    </template>

  </div>

</template>

<script>
import { mapGetters } from 'vuex'
import { authMixin } from '@/mixins'
import ImportDatasets from './ImportDatasets'
import EditRow from '@/components/tables/EditRow'
import TableRow from '@/components/reports/TableRow'
import Combinator from '@/components/datasets/Combinator'
import ProgressBar from '@/components/modules/ProgressBar'
import ColumnHeader from '@/components/columns/ColumnHeader'
import OptionsButtons from '@/components/modules/OptionsButtons'
import ToggleAll from '@/components/modules/mass-remove/ToggleAll'
import LoadingNotification from '@/components/modules/LoadingNotification'

export default {
  name: 'Dataset',
  mixins: [
    authMixin
  ],
  components: {
    EditRow,
    TableRow,
    ToggleAll,
    Combinator,
    ProgressBar,
    ColumnHeader,
    ImportDatasets,
    OptionsButtons,
    LoadingNotification
  },
  data () {
    return {
      copy: null,
      timer: 0,
      loadingTime: 0,
      removables: [],
      sortColumnId: 0,
      datasetId: null,
      duplicate: false,
      filterListHeight: 250,
      importDatasets: false,
      duplicateSource: null,
      showFixedHeader: false,
      editRowIsActive: false,
      combinatorIsActive: false,
      importBundleIsActive: false
    }
  },
  computed: {
    ...mapGetters({
      table: 'tables/table',
      bundle: 'bundles/bundle',
      columns: 'columns/columns',
      account: 'account/account',
      filters: 'filters/filters',
      loading: 'datasets/loading',
      progress: 'tables/progress',
      datasets: 'datasets/datasets',
      refreshing: 'datasets/refreshing',
      withTrashed: 'datasets/withTrashed',
      scrollPosition: 'screen/scrollPosition'
    }),
    channel () {
      return `changes_table_${this.$route.params.tableId}_bundle_${this.$route.params.bundleId}`
    },
    loadingMessage () {
      const header = (this.loadingTime > 5000) ? 'Dit duurt iets langer dan verwacht, nog even geduld alstublieft...' : 'De tabel wordt geladen...'

      // const body = ['Tabel data ophalen van de server']
      // if (this.loading === 2) body.push('Tabel data verwerken voor presentatie'),
      // if (this.loading === 3) body.push('Data synchronisatie initialiseren')
      //
      // return `<h4 class="title is-6">${header}</h4><p>${body.join('<br>')}</p>`
      return header
    },
    loaded () {
      return this.table &&
        this.columns.length > 0
    },
    filteredDatasets () {
      if (this.filters.length === 0) return this.datasets

      return this.datasets.filter((dataset) => {
        return this.filters.map((filter) => {
          const value = dataset.columns.find(x => x.id === filter.columnId).value

          if (!value.id) return filter.showEmpty

          return filter.filter.includes(value.id)
        }).every(pass => pass)
      })
    },
    datasetIds () {
      return this.filteredDatasets.map(x => x.id)
    },
    hasSelected () {
      return this.removables.map((x) => {
        if (x.inFilter) {
          return x.id
        }
      }).filter(x => x)
    },
    allSelected () {
      return (this.filteredDatasets) ? this.filteredDatasets.length === this.removables.length : false
    },
    someSelected () {
      if (this.allSelected) return false

      return this.removables.filter(removable => removable.inFilter).length > 0
    },
    showMassRemoveButton () {
      return this.allSelected === true || this.someSelected === true
    },
    toolbarButtons () {
      const toolbar = [
        {
          icon: 'fas fa-plus',
          style: null,
          title: 'Rij toevoegen',
          click: {
            fnc: this.toggleEditRow,
            params: true
          },
          disabled: this.withTrashed
        }
      ]

      if (this.isAdmin(this.account) || this.inScope(this.account, 11)) {
        toolbar.push({
          icon: 'fas fa-file-import',
          style: null,
          title: 'Rijen importeren uit een andere bundel',
          click: {
            fnc: this.toggleImport,
            params: 'bundle'
          },
          disabled: this.withTrashed
        })

        toolbar.push({
          icon: 'fas fa-upload',
          style: null,
          title: 'Rijen importeren uit Excel-sheet',
          click: {
            fnc: this.toggleImport,
            params: 'file'
          },
          disabled: this.withTrashed
        })
      }

      if (this.isAdmin(this.account)) {
        toolbar.push({
          icon: 'fas fa-download',
          style: null,
          title: 'Tabel exporteren naar Excel',
          click: {
            fnc: this.requestExport,
            params: 'file'
          },
          disabled: this.withTrashed
        })
      }

      if (this.isAdmin(this.account) || this.inScope(this.account, 12)) {
        toolbar.push({
          icon: 'fas fa-mortar-pestle',
          style: null,
          title: 'Rijen genereren vanuit de basislijst',
          click: {
            fnc: this.toggleCombinator,
            params: true
          },
          disabled: this.withTrashed
        })
      }

      toolbar.push({
        icon: 'fas fa-trash',
        style: (this.withTrashed) ? 'is-danger' : null,
        title: 'Toon verwijderde regels',
        click: {
          fnc: this.toggleTrashed,
          params: true
        },
        disabled: true
      })

      // if (this.isAdmin(this.account)) { // Enable middleware in api when limiting this feature to admin
      toolbar.push({
        icon: (this.refreshing === 2) ? 'fas fa-check' : (this.refreshing === 1) ? 'fas fa-sync fa-spin' : 'fas fa-sync',
        style: (this.refreshing === 2) ? 'is-success' : null,
        title: 'Vervang tijdelijk opgeslagen tabel gegevens',
        click: {
          fnc: this.refreshCache,
          params: true
        }
      })
      // }

      return toolbar
    }
  },
  created () {
    this.loadTable()
  },
  mounted () {
    this.$echo.channel(this.channel)
      .listen('DatasetWasCreated', (e) => {
        const dataset = {
          id: e.id,
          columns: e.columns.filter(column => !Number.isInteger(column)).map(column => {
            const raw = JSON.parse(column)

            return {
              id: raw.column_id,
              value: {
                id: raw.id,
                value: raw.value
              }
            }
          })
        }

        this.$store.commit('datasets/PUSH', dataset)
      })

    this.$echo.channel(this.channel)
      .listen('DatasetWasUpdated', (e) => {
        const dataset = {
          id: e.id,
          columns: e.columns.filter(column => !Number.isInteger(column)).map(column => {
            const raw = JSON.parse(column)

            return {
              id: raw.column_id,
              value: {
                id: raw.id,
                value: raw.value
              }
            }
          })
        }

        this.$store.commit('datasets/UPDATE', dataset)
      })

    this.$echo.channel(this.channel)
      .listen('DatasetWasDeleted', (e) => {
        console.log(e);
        this.$store.commit('datasets/REMOVE', e.id)
      })

    this.$echo.channel(this.channel).listen('CacheCreated', () => {
      this.loadDatasets(true)
    })

    this.$nextTick(() => {
      this.setFilterListHeight()
    })

    window.addEventListener('resize', this.setColumnWidths)
    window.addEventListener('resize', this.setFilterListHeight)
  },
  beforeDestroy () {
    this.$store.commit('tables/RESET')
    this.$store.commit('columns/RESET')
    this.$store.commit('datasets/PURGE')

    window.removeEventListener('resize', this.setColumnWidths)
    window.removeEventListener('resize', this.setFilterListHeight)
  },
  watch: {
    table () {
      if (this.table) {
        clearInterval(this.timer)
        this.loadingTime = 0
        this.loadColumns()
      }
    },
    columns () {
      if (this.columns.length !== 0) {
        this.loadDatasets()
      }
    },
    // datasets () {
    //   console.log('something happened')
    //   this.setColumnWidths()
    // },
    filteredDatasets () {
      this.updateRemovables()
    }
  },
  methods: {
    setColumnWidths () {
      const guideWidths = Array.from(document.getElementsByClassName('guide-column')).map(col => col.clientWidth)

      Array.from(document.getElementsByClassName('header-column')).forEach((col, key) => col.setAttribute('style', `width: ${guideWidths[key] + 1}px;`))
    },
    setFilterListHeight () {
      this.filterListHeight = Math.ceil(window.innerHeight * 0.6)
    },
    getDataset (datasetId) {
      return this.datasets.find(x => x.id === datasetId)
    },
    isLastColumn (idx) {
      return idx === this.columns.length - 1
    },
    getColumnHeader (columnId) {
      return {
        column: this.getColumn(columnId),
        values: this.getFilterOptions(columnId)
      }
    },
    getFilterOptions (columnId) {
      const values = this.datasets.flatMap((dataset) => {
        return dataset.column_values.filter(x => x.column_id === columnId)
      })

      return values.map(x => x.value)
    },
    toggleEditRow (status) {
      this.editRowIsActive = status
    },
    toggleCombinator (status) {
      this.combinatorIsActive = status
    },
    toggleImport (status) {
      this.importDatasets = status
    },
    toggleRemove (datasetId) {
      if (this.isRemovable(datasetId)) {
        this.removables.splice(this.getRemovableIndex(datasetId), 1)
      } else {
        this.removables.push({
          id: datasetId,
          inFilter: this.isInFilter(datasetId)
        })
      }
    },
    toggleTrashed () {
      this.$store.commit('datasets/TOGGLE_WITH_TRASHED')
      this.load()
    },
    toggleAllSelected () {
      if (this.allSelected) {
        this.clearRemovables()
      } else {
        this.clearRemovables()
        this.filteredDatasets.forEach(dataset => this.toggleRemove(dataset.id))
      }
    },
    refreshCache () {
      this.$store.dispatch('datasets/refresh', {
        tableId: this.table.id,
        bundleId: this.bundle.id
      })
    },
    updateRemovables () {
      this.removables.forEach((removable) => {
        removable.inFilter = this.isInFilter(removable.id)
      })
    },
    clearRemovables () {
      this.removables = []
    },
    getRemovableIndex (datasetId) {
      return this.removables.findIndex(x => x.id === datasetId)
    },
    isRemovable (datasetId) {
      return this.getRemovableIndex(datasetId) !== -1
    },
    isInFilter (datasetId) {
      return this.filteredDatasets.findIndex(dataset => dataset.id === datasetId) !== -1
    },
    parse (columns) {
      return columns.map(column => column.value.value)
    },
    loadTable () {
      this.timer = setInterval(() => {
        this.loadingTime = this.loadingTime + 500
      }, 500)

      this.$store.dispatch('tables/load', this.$route.params.tableId)
    },
    loadColumns () {
      this.$store.dispatch('columns/load', {
        model: 'table',
        id: this.$route.params.tableId,
        bundleId: this.$route.params.bundleId
      })
    },
    loadDatasets () {
      const columnIds = this.columns.map(column => {
        return column.column.id
      })

      this.$store.dispatch('datasets/load', {
        tableId: this.$route.params.tableId,
        bundleId: this.$route.params.bundleId,
        columnIds: columnIds,
        withTrashed: this.withTrashed
      })
    },
    save (payload) {
      const dataset = {
        id: payload.id,
        tableId: this.table.id,
        bundleId: this.bundle.id,
        columnValues: payload.data
      }

      this.$store.dispatch('datasets/save', dataset)
      this.close()
    },
    edit (datasetId) {
      this.datasetId = datasetId

      this.toggleEditRow(true)
    },
    duplicateFrom (datasetId) {
      this.duplicate = true
      this.edit(datasetId)
    },
    restore (datasetId) {
      this.$store.dispatch('datasets/restore', {
        datasetId: datasetId,
        tableId: this.table.id
      })
    },
    remove (datasetId) {
      this.$store.dispatch('datasets/remove', datasetId)
    },
    massRemove () {
      const removables = this.removables.filter(removable => removable.inFilter).map(removable => removable.id)
      this.$store.dispatch('datasets/massRemove', { removables: removables })

      removables.forEach((removable) => {
        const idx = this.getRemovableIndex(removable)
        this.removables.splice(idx, 1)
      })
    },
    close () {
      this.datasetId = null
      this.duplicate = false
      this.toggleEditRow(false)
    },
    requestExport () {
      this.$store.dispatch('tables/submitExport', {
        table: {
          id: this.table.id,
          name: this.table.name
        },
        bundle: {
          id: this.bundle.id,
          name: this.bundle.name
        }
      })
    }
  }
}
</script>

<style scoped>
  #headerTable {
    position: sticky;
    top: 51px;
    z-index: 39;
    margin-bottom: 0;
    table-layout: fixed;
  }

  #dataTable tr:last-child {
    visibility: hidden;
  }
</style>
